import React, { useEffect, useState } from 'react';
import axios from 'axios';

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [error, setError] = useState('');
    const [editingUser, setEditingUser] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUsers(response.data);
                } catch (err) {
            if (err.response && err.response.data && err.response.data.detail) {
                setError(err.response.data.detail);
            } else {
                setError('Failed to fetch users');
            }
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleDelete = async (userId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${process.env.REACT_APP_API_URL}/users/${userId}`,
            {
                headers: { Authorization: `Bearer ${token}` },
            });
            fetchUsers(); // Refresh users after deletion
        } catch (err) {
            setError('Failed to delete user');
        }
    };

    const handleEdit = (user) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const handleSuspend = async (user) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${process.env.REACT_APP_API_URL}/admin/users/${user._id}`, 
                { is_suspended: !user.is_suspended },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            fetchUsers(); // Refresh users after suspension
        } catch (err) {
            setError('Failed to suspend user');
        }
    };

        const handleUpdateUser = async (e) => {
        console.log("handleUpdateUser function called");
        e.preventDefault();
        try {
            console.log("Submitting update for user:", editingUser);
            const token = localStorage.getItem('token');
            await axios.put(`${process.env.REACT_APP_API_URL}/admin/users/${editingUser._id}`, 
                { role: editingUser.role, verification_status: editingUser.verification_status },
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );
            console.log("Update successful");
            setIsModalOpen(false);
            setEditingUser(null);
            fetchUsers(); // Refresh users after update
                } catch (err) {
            console.error("Error updating user:", err);
            if (err.response && err.response.data && err.response.data.detail) {
                setError(err.response.data.detail);
            } else {
                setError('Failed to update user');
            }
        }
    };

    if (error) {
        return <div className="text-red-500">{error}</div>;
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">User Management</h1>
                <p className="text-slate-500 mt-1">Manage platform users and permissions ({users.length} total)</p>
            </div>

            {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                </div>
            )}

            <div className="card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr>
                                <th className="text-left" style={{ width: '10%' }}>Avatar</th>
                                <th className="text-left" style={{ width: '20%' }}>Name</th>
                                <th className="text-left" style={{ width: '20%' }}>Email</th>
                                <th className="text-left" style={{ width: '12%' }}>Role</th>
                                <th className="text-left" style={{ width: '15%' }}>Verification</th>
                                <th className="text-center" style={{ width: '23%' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.length === 0 ? (
                                <tr>
                                    <td colSpan="6" className="text-center py-8 text-slate-500">
                                        No users found
                                    </td>
                                </tr>
                            ) : (
                                users.map((user) => (
                                    <tr key={user._id}>
                                        <td>
                                            <img 
                                                src={user.profile_picture_url} 
                                                alt={user.name} 
                                                className="h-10 w-10 rounded-full object-cover"
                                            />
                                        </td>
                                        <td>
                                            <p className="font-medium text-slate-900">{user.name}</p>
                                            <p className="text-xs text-slate-500">@{user.username}</p>
                                        </td>
                                        <td>
                                            <p className="text-sm text-slate-600">{user.email}</p>
                                        </td>
                                        <td>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                user.role === 'admin' 
                                                    ? 'bg-purple-100 text-purple-800' 
                                                    : 'bg-slate-100 text-slate-800'
                                            }`}>
                                                {user.role}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                user.verification_status === 'blue' 
                                                    ? 'bg-blue-100 text-blue-800'
                                                    : user.verification_status === 'yellow'
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-gray-100 text-gray-800'
                                            }`}>
                                                {user.verification_status || 'none'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="flex justify-center gap-2">
                                                <button 
                                                    onClick={() => handleEdit(user)} 
                                                    className="px-3 py-1 text-xs font-medium text-white bg-blue-500 rounded hover:bg-blue-600 transition-colors"
                                                >
                                                    Edit
                                                </button>
                                                <button 
                                                    onClick={() => handleSuspend(user)} 
                                                    className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                                                        user.is_suspended
                                                            ? 'text-white bg-green-500 hover:bg-green-600'
                                                            : 'text-white bg-yellow-500 hover:bg-yellow-600'
                                                    }`}
                                                >
                                                    {user.is_suspended ? 'Unsuspend' : 'Suspend'}
                                                </button>
                                                <button 
                                                    onClick={() => handleDelete(user._id)} 
                                                    className="px-3 py-1 text-xs font-medium text-white bg-red-500 rounded hover:bg-red-600 transition-colors"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed z-50 inset-0 overflow-y-auto modal-overlay flex items-center justify-center">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 modal-content animate-slide-in">
                        <form onSubmit={handleUpdateUser}>
                            <div className="px-6 py-4 border-b border-slate-200">
                                <h3 className="text-lg font-semibold text-slate-900">Edit User</h3>
                                <p className="text-sm text-slate-500 mt-1">{editingUser.name}</p>
                            </div>

                            <div className="px-6 py-4 space-y-4">
                                <div>
                                    <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-2">
                                        Role
                                    </label>
                                    <select
                                        id="role"
                                        name="role"
                                        value={editingUser.role}
                                        onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>

                                <div>
                                    <label htmlFor="verification_status" className="block text-sm font-medium text-slate-700 mb-2">
                                        Verification Status
                                    </label>
                                    <select
                                        id="verification_status"
                                        name="verification_status"
                                        value={editingUser.verification_status}
                                        onChange={(e) => setEditingUser({ ...editingUser, verification_status: e.target.value })}
                                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="none">None</option>
                                        <option value="blue">Blue</option>
                                        <option value="yellow">Yellow</option>
                                    </select>
                                </div>
                            </div>

                            <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex gap-3 justify-end">
                                <button 
                                    type="button" 
                                    onClick={() => setIsModalOpen(false)}
                                    className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;